import { Player, type Health } from "../live/player.js";
import { toggleFullscreen } from "./fullscreen.js";

export interface CameraInfo {
  name: string;
  display_name: string;
  /** Состояние из машины живости на сервере: ok / stream_down / offline / … */
  state: string;
  ready: boolean;
}

/**
 * Сетка плиток. Каждая плитка — независимый проигрыватель со своей машиной
 * состояний: отказ одной камеры не должен гасить остальные.
 */
export class Grid {
  private readonly players = new Map<string, Player>();

  constructor(private readonly root: HTMLElement) {}

  render(cameras: CameraInfo[]): void {
    if (cameras.length === 0) {
      this.root.innerHTML =
        '<p class="muted">Ни одной камеры не заведено.</p>';
      return;
    }

    // Убираем плитки исчезнувших камер, чтобы их проигрыватели не молотили в
    // пустоту.
    const alive = new Set(cameras.map((camera) => camera.name));
    for (const [name, player] of this.players) {
      if (!alive.has(name)) {
        void player.stop();
        this.players.delete(name);
        document.getElementById(`tile-${name}`)?.remove();
      }
    }

    for (const camera of cameras) {
      if (this.players.has(camera.name)) {
        this.updateState(camera);
        continue;
      }
      this.root.appendChild(this.tile(camera));
      const video = document.getElementById(
        `video-${camera.name}`,
      ) as HTMLVideoElement;
      const player = new Player(camera.name, video, (health) =>
        this.showHealth(camera.name, health),
      );
      this.players.set(camera.name, player);
      player.start();
    }
  }

  private tile(camera: CameraInfo): HTMLElement {
    const tile = document.createElement("section");
    tile.className = "tile";
    tile.id = `tile-${camera.name}`;
    tile.innerHTML = `
      <div class="tile-head">
        <strong>${escape(camera.display_name || camera.name)}</strong>
        <span class="tile-tools">
          <span class="state" id="state-${camera.name}"></span>
          <button class="tile-full ghost" type="button" aria-label="На весь экран"
                  title="На весь экран">⛶</button>
        </span>
      </div>
      <!-- muted обязателен: без него автовоспроизведение запрещено политикой
           браузера, и картинка не появится вовсе. Звука мы и так не передаём. -->
      <video id="video-${camera.name}" autoplay muted playsinline></video>
      <div class="badge" id="badge-${camera.name}">подключение…</div>
    `;
    this.updateStateIn(tile, camera);
    tile
      .querySelector(".tile-full")
      ?.addEventListener("click", () => toggleFullscreen(tile));
    tile.addEventListener("dblclick", () => toggleFullscreen(tile));
    return tile;
  }

  private updateState(camera: CameraInfo): void {
    const tile = document.getElementById(`tile-${camera.name}`);
    if (tile) this.updateStateIn(tile, camera);
  }

  private updateStateIn(tile: HTMLElement, camera: CameraInfo): void {
    const badge = tile.querySelector(`#state-${camera.name}`);
    if (!badge) return;
    badge.textContent = stateLabel(camera.state);
    badge.className = `state state-${severityOf(camera.state)}`;
  }

  /**
   * Бейдж над картинкой.
   *
   * Он обязан всегда называть транспорт. Принять HLS с задержкой в секунду-две
   * за живую картинку на антикражной системе — настоящая операционная беда, и
   * стоит это ровно одной строки текста.
   */
  private showHealth(camera: string, health: Health): void {
    const badge = document.getElementById(`badge-${camera}`);
    if (!badge) return;

    if (health.transport === "none") {
      badge.textContent = health.state;
      badge.className = "badge badge-warn";
      return;
    }

    const parts: string[] = [];
    parts.push(health.transport === "webrtc" ? "LIVE · WebRTC" : "С ЗАДЕРЖКОЙ · HLS");
    if (health.fps > 0) parts.push(`${health.fps} fps`);
    if (health.width > 0) parts.push(`${health.width}×${health.height}`);
    if (health.rttMs !== null) parts.push(`RTT ${health.rttMs} мс`);
    // Переход на ICE-TCP стоит показывать: это ранний признак, что зрителю
    // скоро понадобится запасной HLS.
    if (health.protocol === "tcp") parts.push("через TCP");

    badge.textContent = parts.join(" · ");
    badge.className =
      health.transport === "webrtc" && health.state === "живой"
        ? "badge badge-ok"
        : "badge badge-warn";
  }

  async stopAll(): Promise<void> {
    await Promise.all([...this.players.values()].map((p) => p.stop()));
    this.players.clear();
  }
}

function stateLabel(state: string): string {
  switch (state) {
    case "ok":
      return "в порядке";
    case "stream_down":
      return "нет видео";
    // Оба случая — просто ошибка связи, без догадок о причине.
    case "heartbeat_down":
    case "offline":
      return "ошибка связи";
    case "planned_down":
      return "плановая остановка";
    default:
      return state;
  }
}

function severityOf(state: string): string {
  switch (state) {
    case "ok":
    case "planned_down":
      return "ok";
    case "offline":
      return "critical";
    case "stream_down":
      return "warn";
    default:
      return "info";
  }
}

function escape(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
